import { useEffect } from 'react';
import {
  TransformComponent,
  TransformWrapper,
  useControls,
} from 'react-zoom-pan-pinch';
import ViewableDirectionIcon from '@/components/ui/icons/trainers/ViewableDirectionIcon';
import { Skeleton } from '@/components/shadcn/skeleton';

export const Icon = ViewableDirectionIcon;

type BoundingBox = [number, number, number, number];

interface BaseProps {
  src: string;
  boundingBox: BoundingBox;
  otherBoundingBoxes?: BoundingBox[];
}

interface MergingProps extends BaseProps {
  imgRef?: any;
  isImageLoaded?: boolean;
  tempImgHeight?: any;
}
interface AccuracyAndAzimuthProps extends BaseProps {
  imgRef: any;
  isImageLoaded: boolean;
  tempImgHeight: any;
}

const Controls = ({ isImageLoaded }: { isImageLoaded: boolean }) => {
  const { resetTransform } = useControls();

  useEffect(() => {
    if (isImageLoaded) {
      resetTransform(0);
    }
  }, [isImageLoaded, resetTransform]);

  return null;
};

export default function ImageView({
  imgRef,
  isImageLoaded,
  src,
  boundingBox: [x, y, w, h],
  tempImgHeight,
  otherBoundingBoxes = [],
}: MergingProps | AccuracyAndAzimuthProps) {
  if (imgRef === undefined || isImageLoaded === undefined) {
    return (
      <TransformWrapper>
        <TransformComponent>
          <img src={src} alt="Frame" />
          <div
            className="absolute border-2 border-electricBlue-600"
            style={{
              left: `${x}%`,
              top: `${y}%`,
              width: `${w}%`,
              height: `${h}%`,
            }}
          />
          {otherBoundingBoxes.map(([x, y, w, h]) => (
            <div
              key={`${x}-${y}`}
              className="absolute border-2 border-neutral-100"
              style={{
                left: `${x}%`,
                top: `${y}%`,
                width: `${w}%`,
                height: `${h}%`,
              }}
            />
          ))}
        </TransformComponent>
      </TransformWrapper>
    );
  }

  return (
    <div className="relative w-full">
      <TransformWrapper>
        <Controls isImageLoaded={isImageLoaded} />
        <TransformComponent>
          {src ? (
            <div
              data-image-loaded={isImageLoaded}
              className="hidden data-[image-loaded=true]:block">
              <img className="w-full" ref={imgRef} src={src} alt="Frame" />
            </div>
          ) : null}
          {isImageLoaded ? (
            <div
              className="absolute border-2 border-electricBlue-600"
              style={{
                left: `${x}%`,
                top: `${y}%`,
                width: `${w}%`,
                height: `${h}%`,
              }}
            />
          ) : (
            <Skeleton
              className="h-[350px] w-full border border-l-0 border-neutral-700 bg-neutral-800"
              style={{
                height: tempImgHeight || 350,
              }}
            />
          )}
        </TransformComponent>
      </TransformWrapper>
    </div>
  );
}
